import { useEffect, useState, useCallback, useRef } from "react";

export const useInfiniteScroll = (
  fetchFn: any,
  dependencies: any[] = [],
  initialPage = 0,
  uniqueKey = "uuid",
  // Most endpoints return their page at `data.content`. A few nest it under a
  // named section instead -- the lab test catalog returns
  // `data.testCodes.content` alongside `data.orderSets.content` -- and without
  // a way to point at it the hook found no `content`, accumulated nothing and
  // paging silently did nothing. Pass a selector for those.
  selectPage?: (response: any) => any,
) => {
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState<any[]>([]);
  const [totalElements, setTotalElements] = useState<number | null>(null);

  // When a dependency (a search term, a filter) changes, `page` still holds
  // the old list's page until the reset effect below runs after this render.
  // Fetching with it requested that page of the *new* list -- page 3 of a
  // fresh search -- before the reset asked for page 0. Treat a render whose
  // dependencies just changed as already on the first page.
  const prevDependenciesRef = useRef(dependencies);
  const dependenciesChanged =
    dependencies.length !== prevDependenciesRef.current.length ||
    dependencies.some(
      (value, index) => !Object.is(value, prevDependenciesRef.current[index]),
    );
  const fetchPage = dependenciesChanged ? initialPage : page;

  const { data, isSuccess, isFetching, isFetched, refetch, dataUpdatedAt } =
    fetchFn(fetchPage);

  // Held in a ref so an inline selector does not re-run the effect every render.
  const selectPageRef = useRef(selectPage);
  selectPageRef.current = selectPage;

  useEffect(() => {
    prevDependenciesRef.current = dependencies;
    setPage(initialPage);
    setOptions([]);
    setHasMore(true);
    setTotalElements(null);
    isLoadingMoreRef.current = false;
  }, [...dependencies]);

  useEffect(() => {
    const pageData = selectPageRef.current
      ? selectPageRef.current(data)
      : data?.data;

    if (Array.isArray(pageData?.content) && isSuccess) {
      const newData = pageData.content;

      if (typeof pageData?.totalElements === "number") {
        setTotalElements(pageData.totalElements);
      }

      setOptions((prevOptions) => {
        if (page === initialPage) {
          return newData;
        }
        // Only dedupe on items that actually carry the unique key. When the
        // key field is missing (e.g. the configured uniqueKey doesn't match
        // the response's id field), every item used to resolve to `undefined`
        // and the whole new page was silently dropped instead of appended.
        const existingIds = new Set(
          prevOptions
            .map((item) => item?.[uniqueKey])
            .filter((id) => id !== undefined && id !== null),
        );
        const filteredData = newData.filter((item) => {
          const id = item?.[uniqueKey];
          if (id === undefined || id === null) return true;
          return !existingIds.has(id);
        });
        return [...prevOptions, ...filteredData];
      });

      // Spring pages say whether they are the last one. Trusting that stops
      // the scroll after the final page; waiting for an empty page cost one
      // more request every time a list was scrolled to its end.
      if (newData.length === 0 || pageData?.last === true) {
        setHasMore(false);
      }
    }
    // `dataUpdatedAt` is in here as well as `data` because React Query's
    // structural sharing hands back the *previous* `data` object whenever a
    // refetch comes back deeply equal to what is cached. Keyed on `data` alone
    // the accumulator then never re-runs, so a list that was rebuilt from a
    // socket push (or any other forced refetch) kept showing the rows it
    // already had even though the request had gone out and returned.
    // `dataUpdatedAt` changes on every successful fetch, so the accumulator
    // always gets a chance to apply the response.
    //
    // Re-running is safe in both branches: on the first page the rows are
    // replaced outright, and on later pages the dedupe above drops anything
    // already held, so an identical response is a no-op.
  }, [data, dataUpdatedAt, isSuccess, uniqueKey, page, initialPage]);

  const isFetchingRef = useRef(isFetching);
  const hasMoreRef = useRef(hasMore);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    isFetchingRef.current = isFetching;
    if (!isFetching) {
      isLoadingMoreRef.current = false;
    }
  }, [isFetching]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const handleScroll = useCallback(() => {
    if (
      !isFetchingRef.current &&
      !isLoadingMoreRef.current &&
      hasMoreRef.current
    ) {
      isLoadingMoreRef.current = true;
      setPage((prevPage) => prevPage + 1);
    }
  }, []);
  const refetchAll = useCallback(async () => {
    setPage(initialPage);

    setHasMore(true);
    setTotalElements(null);
    await refetch();
  }, [initialPage, refetch]);

  // Back to the first page without refetching the current one — for callers
  // whose query is invalidated elsewhere. Switching pages fetches the first
  // page by itself.
  const resetPage = useCallback(() => {
    if (page === initialPage) return;
    setPage(initialPage);
    setHasMore(true);
  }, [page, initialPage]);

  return {
    options,
    handleScroll,
    isSuccess,
    isFetching,
    hasMore,
    isFetched,
    refetch: refetchAll,
    resetPage,
    totalElements,
  };
};
